import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { HelpCircle, Clock, MessageSquare, CheckCircle, XCircle, Tag, Settings } from 'lucide-react';

interface AskQuestionNodeData {
  title?: string;
  question?: string;
  variable?: string;
  timeout?: number;
  color?: string;
  tags?: string[];
  keywords?: string[];
  responseType?: string;
  options?: string[];
  min?: number;
  max?: number;
}

export const AskQuestionNode = memo(({ data, isConnectable }: NodeProps<AskQuestionNodeData>) => {
  return (
    <div className="px-4 py-2 shadow-md rounded-md bg-white border-2 border-indigo-200 hover:border-indigo-400 transition-colors duration-300">
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        className="w-3 h-3 bg-indigo-500"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <HelpCircle className="h-4 w-4 mr-2" style={{ color: data.color || '#6366f1' }} />
          <div className="text-sm font-medium text-gray-900">
            {data.title || 'Hacer Pregunta'}
          </div>
        </div>
        <Settings className="h-4 w-4 text-gray-400" />
      </div>
      <div className="mt-2 text-sm text-gray-600">
        <div className="font-medium">{data.question || 'Sin pregunta configurada'}</div>
        
        <div className="mt-2 space-y-2">
          {/* Tiempo de espera y Variable */}
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center">
              <Clock className="h-3 w-3 mr-1" />
              <span>Tiempo: {data.timeout || 1} min</span>
            </div>
            {data.variable && (
              <div className="flex items-center">
                <MessageSquare className="h-3 w-3 mr-1" />
                <span>Variable: ${data.variable}</span>
              </div>
            )}
          </div>

          {/* Palabras clave */}
          {data.keywords && data.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {data.keywords.map((keyword, index) => (
                <span 
                  key={index}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {keyword}
                </span>
              ))}
            </div>
          )}

          {/* Acciones */}
          <div className="border-t pt-2 mt-2">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center text-green-600">
                <CheckCircle className="h-3 w-3 mr-1" />
                <span>Respuesta recibida</span>
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="match"
                  style={{ left: '25%' }}
                  isConnectable={isConnectable}
                  className="w-3 h-3 bg-green-500"
                />
              </div>
              <div className="flex items-center text-red-600">
                <XCircle className="h-3 w-3 mr-1" />
                <span>Sin respuesta ({data.timeout || 1} min)</span>
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="timeout"
                  style={{ left: '75%' }}
                  isConnectable={isConnectable}
                  className="w-3 h-3 bg-red-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

AskQuestionNode.displayName = 'AskQuestionNode';